// Ustawienia konta -> Serwisy QSO: dane logowania i klucze API do serwisow, do
// ktorych HamLogger wysyla QSO (QRZ Logbook, eQSL, ClubLog, LoTW, HRDLOG,
// Wavelog). Sama wysylka (przycisk "Wyslij" i harmonogram automatycznej
// wysylki) jest w widoku "Wyslij do serwisow" -- tu tylko zapis danych.
// Hasla i klucze nigdy nie wracaja z serwera: widac tylko, ze sa zapisane.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Icon from './icons';
import { tx } from './i18n';

const API = process.env.REACT_APP_API_URL || '';

// Pola kluczy API: type="text" + maskowanie CSS (input--secret), zeby
// menedzer hasel przegladarki NIE wpisywal tu hasla do HamLoggera.
const NO_AUTOFILL = { autoComplete: 'off', spellCheck: false, 'data-lpignore': 'true', 'data-1p-ignore': 'true', 'data-bwignore': 'true' };

export const SERVICES = [
  { id: 'qrz', name: 'QRZ Logbook', hint: tx('Klucz Logbook API (inny niż login do wyszukiwania znaków) znajdziesz na stronie swojego logbooka na QRZ.com, przycisk „API Key”. Wymaga subskrypcji QRZ „XML Logbook Data”.') },
  { id: 'eqsl', name: 'eQSL.cc', hint: tx('Login i hasło do eQSL.cc. QTH Nickname podaj, jeśli masz w eQSL kilka lokalizacji.') },
  { id: 'clublog', name: 'ClubLog', hint: tx('Klucz API, email i hasło z konta ClubLog.') },
  { id: 'lotw', name: 'LoTW', hint: tx('Certyfikat stacji (.tq6) z lotw.arrl.org służy do wysyłki QSO — wgrywasz go raz. Login i hasło do strony lotw.arrl.org (opcjonalnie) pozwalają co 6 godzin pobierać nowe potwierdzenia. Wszystko jest szyfrowane na serwerze.') },
  { id: 'hrdlog', name: 'HRDLOG.net', hint: tx('Kod wysyłki (Upload code), który HRDLOG przysyła e-mailem po rejestracji — to nie jest hasło do konta. Login to Twój znak.') },
  { id: 'wavelog', name: 'Wavelog / Cloudlog', hint: tx('Adres Twojego Wavelog/Cloudlog, klucz API z prawem zapisu (Konto → API Keys) i ID profilu stacji (Station Locations).') },
];

// Czy dane do serwisu sa zapisane (wspolne dla ustawien i widoku wysylki)
export function serviceConfigured(id, auto, userProfile) {
  if (id === 'qrz') return !!userProfile?.has_qrz_logbook_key;
  return !!auto?.[id]?.has_saved_creds;
}

function SecretInput({ value, onChange, placeholder, name }) {
  const [show, setShow] = useState(false);
  return (
    <div className="svc-secret">
      <input className={'input' + (show ? '' : ' input--secret')} type="text" name={name} {...NO_AUTOFILL}
        value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShow(s => !s)}
        title={show ? tx('Ukryj klucz') : tx('Pokaż klucz')} aria-label={show ? tx('Ukryj klucz') : tx('Pokaż klucz')}>
        <Icon name={show ? 'lock' : 'eye'} size={15} />
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="form-group"><label>{label}</label>{children}</div>;
}

export default function ServiceAccounts({ notify, userProfile, setUserProfile, onOpenUpload }) {
  const [auto, setAuto] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({});
  const set = (k) => (v) => setF(s => ({ ...s, [k]: v }));

  const load = useCallback(() => {
    axios.get(`${API}/api/upload/auto-settings`).then(r => setAuto(r.data)).catch(() => setAuto({}));
  }, []);
  useEffect(() => { load(); }, [load]);
  const refreshProfile = () => axios.get(`${API}/api/auth/me`).then(r => setUserProfile?.(r.data)).catch(() => {});

  const toggle = (id) => {
    if (open === id) { setOpen(null); return; }
    const a = auto?.[id] || {};
    setF({
      username: a.username || '', qth_nickname: a.qth_nickname || '', email: a.email || '',
      url: a.url || '', station_id: a.station_id || '', station_callsign: a.station_callsign || userProfile?.callsign || '',
      password: '', api_key: '', key: '', cert: null, cert_password: '',
      lotw_user: a.username || '', lotw_pass: '',
    });
    setOpen(id);
  };

  const save = async (id) => {
    setBusy(true);
    try {
      if (id === 'qrz') {
        if (!f.key.trim()) { notify(tx('Wpisz klucz QRZ Logbook API'), 'error'); setBusy(false); return; }
        await axios.put(`${API}/api/auth/update`, { qrz_logbook_key: f.key.trim() });
        await refreshProfile();
      } else if (id === 'lotw') {
        if (!f.cert && !f.lotw_user.trim()) { notify(tx('Wybierz plik certyfikatu (.tq6) albo podaj login do lotw.arrl.org'), 'error'); setBusy(false); return; }
        if (f.lotw_user.trim()) {
          await axios.put(`${API}/api/upload/credentials/lotw`, { username: f.lotw_user.trim(), password: f.lotw_pass });
        }
        if (!f.cert) {
          notify(tx('Zapisano dane: {0}', ['LoTW'])); setOpen(null); load(); setBusy(false); return;
        }
        const certBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
          reader.onerror = reject;
          reader.readAsDataURL(f.cert);
        });
        const r = await axios.post(`${API}/api/lotw/certificate`, {
          cert_base64: certBase64, cert_filename: f.cert.name, cert_password: f.cert_password, station_callsign: f.station_callsign,
        });
        if (!r.data.ok) { notify(tx('Import certyfikatu nie powiodl sie: {0}', [r.data.output || r.data.error || '']), 'error'); setBusy(false); return; }
      } else {
        await axios.put(`${API}/api/upload/credentials/${id}`, f);
      }
      notify(tx('Zapisano dane: {0}', [SERVICES.find(s => s.id === id).name]));
      setOpen(null);
      load();
    } catch (err) { notify(err.response?.data?.error || tx('Błąd zapisu'), 'error'); }
    setBusy(false);
  };

  const remove = async (id) => {
    const name = SERVICES.find(s => s.id === id).name;
    if (!window.confirm(tx('Usunąć zapisane dane logowania do {0}? Automatyczna wysyłka zostanie wyłączona.', [name]))) return;
    try {
      if (id === 'lotw') {
        if (auto?.lotw?.has_saved_creds) await axios.delete(`${API}/api/lotw/certificate`);
        if (auto?.lotw?.has_login) await axios.delete(`${API}/api/upload/credentials/lotw`);
      } else await axios.delete(`${API}/api/upload/credentials/${id}`);
      if (id === 'qrz') await refreshProfile();
      notify(tx('Usunięto dane: {0}', [name]));
      setOpen(null);
      load();
    } catch (err) { notify(err.response?.data?.error || tx('Błąd'), 'error'); }
  };

  const saved = (id) => serviceConfigured(id, auto, userProfile);
  const anySaved = (id) => saved(id) || (id === 'lotw' && !!auto?.lotw?.has_login);
  const keyPh = (id) => saved(id) ? tx('Zapisany — wpisz nowy tylko, by go zmienić') : '';

  const form = (id) => {
    switch (id) {
      case 'qrz': return (
        <>
          <Field label={tx('QRZ Logbook API Key')}>
            <SecretInput name="qrz-logbook-api-key" value={f.key} onChange={set('key')}
              placeholder={saved('qrz') ? keyPh('qrz') : tx('np. ABCD-0A0B-1C1D-2E2F')} />
          </Field>
          {saved('qrz') && userProfile?.qrz_logbook_key_hint && (
            <p className="acc-section__hint">{tx('Zapisany klucz kończy się na: {0}', ['…' + userProfile.qrz_logbook_key_hint])}</p>
          )}
          {saved('qrz') && userProfile?.qrz_logbook_key_ok === false && (
            <p className="svc-error">{tx('Zapisany klucz ma zły format (prawdopodobnie przeglądarka wpisała tu hasło) — wpisz klucz QRZ Logbook ponownie.')}</p>
          )}
        </>
      );
      case 'eqsl': return (
        <>
          <div className="form-row">
            <Field label={tx('Nazwa użytkownika eQSL')}>
              <input className="input" value={f.username} onChange={e => set('username')(e.target.value)} placeholder={tx('Twoj znak')} autoComplete="off" />
            </Field>
            <Field label={tx('Hasło eQSL')}>
              <input className="input" type="password" autoComplete="new-password" value={f.password}
                onChange={e => set('password')(e.target.value)} placeholder={saved('eqsl') ? tx('Zapisane — wpisz, by zmienić') : ''} />
            </Field>
          </div>
          <Field label={tx('QTH Nickname')}>
            <input className="input" value={f.qth_nickname} onChange={e => set('qth_nickname')(e.target.value)} placeholder={tx('Domyslny QTH nickname z konta eQSL')} />
          </Field>
        </>
      );
      case 'clublog': return (
        <>
          <Field label={tx('ClubLog API Key')}>
            <SecretInput name="clublog-api-key" value={f.api_key} onChange={set('api_key')}
              placeholder={auto?.clublog?.has_api_key ? tx('Zapisany — wpisz nowy tylko, by go zmienić') : tx('Z ustawien konta ClubLog')} />
          </Field>
          <div className="form-row">
            <Field label={tx('Email ClubLog')}>
              <input className="input" type="email" value={f.email} onChange={e => set('email')(e.target.value)} autoComplete="off" />
            </Field>
            <Field label={tx('Hasło ClubLog')}>
              <input className="input" type="password" autoComplete="new-password" value={f.password}
                onChange={e => set('password')(e.target.value)} placeholder={saved('clublog') ? tx('Zapisane — wpisz, by zmienić') : ''} />
            </Field>
          </div>
        </>
      );
      case 'lotw': return (
        <>
          {saved('lotw') && auto?.lotw?.cert_filename && (
            <p className="acc-section__hint">{tx('Zapisany certyfikat: {0}', [auto.lotw.cert_filename])}</p>
          )}
          <Field label={tx('Certyfikat stacji (.tq6)')}>
            <input className="input" type="file" accept=".tq6,.p12" onChange={e => set('cert')(e.target.files?.[0] || null)} />
          </Field>
          <div className="form-row">
            <Field label={tx('Hasło certyfikatu')}>
              <input className="input" type="password" autoComplete="new-password" value={f.cert_password}
                onChange={e => set('cert_password')(e.target.value)} placeholder={tx('Haslo nadane przy pobieraniu certyfikatu z LoTW')} />
            </Field>
            <Field label={tx('Znak stacji')}>
              <input className="input" value={f.station_callsign} onChange={e => set('station_callsign')(e.target.value.toUpperCase())}
                placeholder={tx('Znak, dla ktorego wystawiono certyfikat')} />
            </Field>
          </div>
          <h5 className="svc-sub">{tx('Pobieranie potwierdzeń (opcjonalnie)')}</h5>
          <div className="form-row">
            <Field label={tx('Login do lotw.arrl.org')}>
              <input className="input" value={f.lotw_user} onChange={e => set('lotw_user')(e.target.value)} autoComplete="off" spellCheck={false} />
            </Field>
            <Field label={tx('Hasło do lotw.arrl.org')}>
              <input className="input" type="password" autoComplete="new-password" value={f.lotw_pass}
                onChange={e => set('lotw_pass')(e.target.value)} placeholder={auto?.lotw?.has_login ? tx('Zapisane — wpisz, by zmienić') : ''} />
            </Field>
          </div>
          {auto?.lotw?.last_download_error && <p className="svc-error">{auto.lotw.last_download_error}</p>}
        </>
      );
      case 'hrdlog': return (
        <Field label={tx('Kod wysyłki HRDLOG (Upload code)')}>
          <SecretInput name="hrdlog-api-key" value={f.api_key} onChange={set('api_key')} placeholder={keyPh('hrdlog')} />
        </Field>
      );
      case 'wavelog': return (
        <>
          <Field label={tx('Adres Wavelog/Cloudlog')}>
            <input className="input" value={f.url} onChange={e => set('url')(e.target.value)} placeholder="https://log.twojadomena.pl" />
          </Field>
          <div className="form-row">
            <div className="form-group form-group--lg"><label>{tx('Klucz API')}</label>
              <SecretInput name="wavelog-api-key" value={f.api_key} onChange={set('api_key')} placeholder={keyPh('wavelog')} />
            </div>
            <Field label={tx('ID profilu stacji')}>
              <input className="input" value={f.station_id} onChange={e => set('station_id')(e.target.value)} placeholder="1" />
            </Field>
          </div>
          <p className="acc-section__hint">{tx('Adres w sieci lokalnej (np. 10.x/192.168.x) może ustawić tylko administrator serwera.')}</p>
        </>
      );
      default: return null;
    }
  };

  return (
    <section className="acc-section">
      <h4 className="acc-section__title">{tx('Serwisy do wysyłki QSO')}</h4>
      <p className="acc-section__hint">
        {tx('Zapisz tu dane logowania i klucze API. Wysyłkę i harmonogram automatycznej wysyłki ustawisz w widoku „Wyślij do serwisów”. Hasła i klucze są zaszyfrowane na serwerze i nie są nigdzie wyświetlane.')}
      </p>
      <div className="svc-list">
        {SERVICES.map(s => (
          <div key={s.id} className={'svc-item' + (open === s.id ? ' svc-item--open' : '')}>
            <div className="svc-item__head">
              <span className="svc-item__name">{s.name}</span>
              {auto === null ? <span className="acc-badge">…</span> : saved(s.id)
                ? <span className="acc-badge acc-badge--ok">{tx('zapisane')}</span>
                : <span className="acc-badge acc-badge--muted">{tx('brak danych')}</span>}
              {s.id === 'lotw' && auto?.lotw?.has_login && <span className="acc-badge acc-badge--info">{tx('potwierdzenia')}</span>}
              <span className="svc-item__spacer" />
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => toggle(s.id)} aria-expanded={open === s.id}>
                <Icon name={open === s.id ? 'x' : (anySaved(s.id) ? 'edit' : 'key')} size={15} />
                {open === s.id ? tx('Zamknij') : anySaved(s.id) ? tx('Zmień') : tx('Ustaw')}
              </button>
            </div>
            {open === s.id && (
              <form className="svc-item__body" onSubmit={e => { e.preventDefault(); save(s.id); }}>
                <p className="acc-section__hint">{s.hint}</p>
                {form(s.id)}
                <div className="acc-actions">
                  <button type="submit" className="btn btn--primary btn--sm" disabled={busy}>
                    <Icon name="save" size={15} /> {busy ? '…' : tx('Zapisz')}
                  </button>
                  {anySaved(s.id) && (
                    <button type="button" className="btn btn--ghost btn--sm acc-danger-ghost" onClick={() => remove(s.id)}>
                      <Icon name="trash" size={15} /> {tx('Usuń dane')}
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        ))}
      </div>
      {onOpenUpload && (
        <button type="button" className="btn btn--ghost btn--sm" style={{ marginTop: 12 }} onClick={onOpenUpload}>
          <Icon name="upload" size={15} /> {tx('Otwórz wysyłkę do serwisów')}
        </button>
      )}
    </section>
  );
}
